import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { Snackbar } from "@material-ui/core";
import _ from "lodash";

import { editCreative } from "../../actions/app/creatives";
import { createName } from "../../utils/creative";
import { insertCreatives } from "../../services/dcm/creatives";
import CreativeNameGenerator from "./CreativeNameGenerator";

// default state objects

// these objects are provided to prevent 'undefined'
// errors when reading the properties of the objects

const defaultErrorObject = {
  isError: false,
  urlId: { isError: false, message: "" },
  make: { isError: false, message: "" },
  modelYear: { isError: false, message: "" },
  model: { isError: false, message: "" },
  trim: { isError: false, message: "" },
  creativeAgency: { isError: false, message: "" },
  strategy: { isError: false, message: "" },
  retailOfferType: { isError: false, message: "" },
  creativeUnit: { isError: false, message: "" },
  extraInfo: { isError: false, message: "" },
  format: { isError: false, message: "" },
  country: { isError: false, message: "" },
  language: { isError: false, message: "" },
  creativeVersion: { isError: false, message: "" },
  dimensions: { isError: false, message: "" },
  creativeDisplay: { isError: false, message: "" }
};

const defaultTaxonomy = { name: "" };

const regExp = /"|»|_|\+|#|&|\.|\//g;

const validateTaxonomy = (
  taxonomy,
  standalone = false,
  isDcmCreative = false,
  pathname = "",
  fileType = ""
) => {
  let validTaxonomy = [];
  if (standalone) {
    validTaxonomy = [
      "make",
      "modelYear",
      "model",
      "trim",
      "creativeAgency",
      "strategy",
      "retailOfferType",
      "creativeUnit",
      "extraInfo",
      "format",
      "country",
      "language",
      "creativeVersion",
      "dimensions",
      "creativeDisplay"
    ];
  } else {
    validTaxonomy = [
      "urlId",
      "make",
      "modelYear",
      "model",
      "trim",
      "creativeAgency",
      "strategy",
      "retailOfferType",
      "creativeUnit",
      "extraInfo",
      "format",
      "country",
      "language",
      "creativeVersion",
      "dimensions",
      "creativeDisplay"
    ];
  }

  let errors = _.defaultsDeep({}, defaultErrorObject); //set errors to default object

  if (isDcmCreative) return errors;

  for (let property of validTaxonomy) {
    //if taxonomy object doesn't include property
    if (
      property !== "trim" &&
      property !== "extraInfo" &&
      property !== "urlId"
    ) {
      if (!taxonomy[property]) {
        if (!errors.isError) errors.isError = true; // set error flag for error object
        errors[property] = { isError: true, message: "Field missing" }; // set error for taxonomy missing
      }
    }
    if (property !== "urlId" && regExp.test(taxonomy[property])) {
      if (!errors.isError) errors.isError = true; // set error flag for error object
      errors[property] = {
        isError: true,
        message: '" _ + # & . / and » characters are not permitted'
      }; // set error for prohibited characters
    }

    if (property === "urlId") {
      if (pathname.includes("preloader")) {
        if (fileType.includes("zip")) {
          if (!taxonomy[property]) {
            if (!errors.isError) errors.isError = true; // set error flag for error object
            errors[property] = { isError: true, message: "Field missing" }; // set error for taxonomy missing
          }
        }
      }

      if (pathname.includes("display")) {
        if (!taxonomy[property]) {
          if (!errors.isError) errors.isError = true; // set error flag for error object
          errors[property] = { isError: true, message: "Field missing" }; // set error for taxonomy missing
        }
      }
    }
  }

  return errors;
};

const CreativeNameManager = ({ standalone }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  let [errors, setErrors] = useState(defaultErrorObject);
  let [taxonomy, setTaxonomy] = useState(defaultTaxonomy); //default state
  let [weight, setWeight] = useState(0);

  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  let creativesState = useSelector((state) => state.creatives);
  let selectedIds = creativesState.selectedIds;
  let selectedCreative = creativesState.byId[creativesState.selectedId];

  useEffect(() => {
    if (creativesState.allIds.length > 0) {
      for (let creative of Object.values(creativesState.byId)) {
        if (creative.dimensions !== "N/A" && !creative.taxonomy.dimensions) {
          let updatedCreative = _.defaultsDeep({}, creative);
          updatedCreative.taxonomy.dimensions = updatedCreative.dimensions
            .split(" ")
            .join("");
           dispatch(editCreative(updatedCreative)); 
        }   
      }
    }
  }, [creativesState, dispatch]);

  useEffect(() => {
    if (selectedCreative) {
      setTaxonomy(selectedCreative.taxonomy); //set taxonomy state based on if creative is selected
      setWeight(selectedCreative.weight);
    } else {
      setTaxonomy(defaultTaxonomy);
      setWeight(0);
    }
    setErrors(defaultErrorObject);
  }, [selectedCreative, creativesState]);

  const handleSelect = (option, action) => {
    let taxonomyValues = { ...taxonomy }; //copy state

    if (option.kind === "newDcmLandingPage") {
      taxonomyValues[action.name] = option.url;
    } else if (option.kind) {
      taxonomyValues[action.name] = option.id; //if it's from DCM, use the DCM ID as the value
    } else {
      taxonomyValues[action.name] = option.value; //set object property based on <Select /> name
    }

    if (selectedCreative.dcmCreative) {
      setTaxonomy(taxonomyValues); // Existing creatives already have taxonomy name, we do not update it
    } else {
      setTaxonomy(createName(taxonomyValues)); //create name and include in taxonomy object
    }
  };

  const handleTextInput = (event) => {
    let taxonomyValues = { ...taxonomy };
    taxonomyValues[event.target.name] = event.target.value;
    setTaxonomy(createName(taxonomyValues));
  };

  const setAlert = (message) => {
    setSnackbarOpen(true);
    setSnackbarMessage(message);
  };

  const handleClearValues = () => {
    setTaxonomy(defaultTaxonomy); //set select values back to default state
  };

  const handleSave = () => {
    for (let id of selectedIds) {
      let currentCreative = _.defaultsDeep({}, creativesState.byId[id]);
      let updatedCreative = { taxonomy: {} };

      setErrors(defaultErrorObject); //reset errors before checking

      if (currentCreative.creativeRole === "PRIMARY") {
        let isDcmCreative = currentCreative && currentCreative.dcmCreative;
        let dimensionsMatch =
          currentCreative.taxonomy.dimensions === taxonomy.dimensions;
        let fileType = "";

        if (!isDcmCreative) {
          fileType = currentCreative.file.type;
        }

        let errors = validateTaxonomy(
          taxonomy,
          standalone,
          isDcmCreative,
          location.pathname,
          fileType
        ); // check taxonomy for errors

        if (!errors.isError) {
          let taxonomyCopy = _.defaultsDeep({}, taxonomy);

          taxonomyCopy.externalId = ""

          if (isDcmCreative) {
            //ignore name and only update URL when the creative is from DCM
            updatedCreative = {
              ...currentCreative,
              taxonomy: {
                ...currentCreative.taxonomy,
                urlId: taxonomyCopy.urlId
              }
            };
          } else {
            updatedCreative = {
              ...currentCreative,
              taxonomy: taxonomyCopy
            };

            if (currentCreative.dimensions !== "N/A" && !dimensionsMatch) {
              //revert dimensions
              updatedCreative.taxonomy.dimensions =
                currentCreative.taxonomy.dimensions;
            }

            if (currentCreative.externalId && currentCreative.externalId.length > 0) {
              updatedCreative.taxonomy.externalId = currentCreative.externalId;
            }

            updatedCreative.taxonomy = createName(updatedCreative.taxonomy);
          }

          dispatch(editCreative(updatedCreative)); //update object in redux store
          if (updatedCreative.backupId) {
            // update backup with same taxonomy name
            let updatedBackupCreative =
              creativesState.byId[updatedCreative.backupId];
            updatedBackupCreative.taxonomy = updatedCreative.taxonomy;
            dispatch(editCreative(updatedBackupCreative));
          }
          setErrors(defaultErrorObject); //reset form errors
          setAlert("Taxonomy saved successfully!");
        } else {
          setErrors(errors);
          setAlert(
            "There was a problem with the taxonomy. Please update and try again."
          );
        }
      }
    }
  };

  const handleSubmit = () => {
    insertCreatives(
      Object.values(creativesState.byId),
      creativesState.assignedPlacements,
      "DISPLAY"
    );
  };

  const handleFileDimensions = () => {
    if (selectedCreative && selectedCreative.dimensions !== "N/A") {
      let trimmedDimensions = selectedCreative.dimensions.split(" ").join("");
      return trimmedDimensions;
    }

    return undefined;
  };

  return (
    <div style={{ width: "100%" }}>
      <CreativeNameGenerator
        taxonomy={taxonomy}
        handleSelect={handleSelect}
        handleClearValues={handleClearValues}
        handleSave={handleSave}
        handleSubmit={handleSubmit}
        handleTextInput={handleTextInput}
        isDisabled={!selectedCreative}
        errors={errors}
        isError={errors.isError}
        standalone={standalone}
        isDcmCreative={selectedCreative && selectedCreative.dcmCreative}
        setAlert={setAlert}
        fileDimensions={handleFileDimensions()}
        selectedCreative={selectedCreative}
      />
      <Snackbar
        message={<span>{snackbarMessage}</span>}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        autoHideDuration={3000}
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        color="success"
        disableWindowBlurListener
      />
    </div>
  );
};

export default CreativeNameManager;
